import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Camera, FileText, Calendar, X, Upload, CheckCircle, AlertCircle, ChevronDown, Wifi, WifiOff, RefreshCw, Loader } from 'lucide-react';
import { ThemeColors, BorderRadius, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { FileStorageService, DetailedUploadProgress, FileUploadStatus as FileUploadStatusType } from '../../services/FileStorageService';
import { ImageCompressionService } from '../../services/ImageCompressionService';

interface CompetitorOption {
  id: string;
  name: string;
  isChecked: boolean;
  photos: File[];
  stockLevel: number;
  displayPanels?: number;
}

export interface CompetitorData {
  id: string;
  name: string;
  photos: File[];
  stockLevel: number;
  displayPanels?: number;
}

export type VisitType = 'setup' | 'maintenance' | 'requested' | 'other' | '';

export const VISIT_TYPE_OPTIONS: { value: VisitType; labelKey: string; defaultLabel: string }[] = [
  { value: '', labelKey: 'notes.selectVisitType', defaultLabel: 'Select Visit Type...' },
  { value: 'setup', labelKey: 'notes.visitTypeSetup', defaultLabel: 'Setup Visit' },
  { value: 'maintenance', labelKey: 'notes.visitTypeMaintenance', defaultLabel: 'Maintenance Visit' },
  { value: 'requested', labelKey: 'notes.visitTypeRequested', defaultLabel: 'Store/Manager Requested Visit' },
  { value: 'other', labelKey: 'notes.visitTypeOther', defaultLabel: 'Other' },
];

interface NotesSectionProps {
  onSubmitNote: (note: string, salesRepName: string, selectedCompetitors: string[], attachments: { media: File[], documents: File[] }, nextVisitDate: string, compressionEnabled?: boolean, storeLevel?: number, lowStockCategories?: string[], requestManagerContact?: boolean, competitorData?: CompetitorData[], visitType?: VisitType, visitReason?: string, onUploadProgress?: (progress: DetailedUploadProgress) => void) => Promise<void>;
  defaultSalesRepName?: string;
  storeId?: string;
}

const Card = styled.div`
  background-color: ${ThemeColors.background.cards};
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.card};
  overflow: hidden;
  border: 1px solid ${ThemeColors.border.light};
  width: 100%;
  max-width: 100%;
`;

const CardHeader = styled.div`
  padding: ${Spacing.md};
  background-color: ${ThemeColors.primary.main};
  color: ${ThemeColors.primary.contrastText};
  position: relative;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  font-family: ${Typography.fontFamily.secondary};
`;

const CardBody = styled.div`
  padding: ${Spacing.lg};
  
  @media (max-width: 768px) {
    padding: ${Spacing.md};
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${Spacing.md};
  width: 100%;
  
  @media (min-width: 769px) {
    margin-bottom: ${Spacing.sm};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.md};
  width: 100%;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: ${Spacing.sm};
  }
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
`;

const Input = styled.input`
  width: 100%;
  padding: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm};
    border-radius: ${BorderRadius.sm};
  }
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
  
  &::placeholder {
    color: ${ThemeColors.text.hint};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  transition: all 0.2s ease;
  min-height: 120px;
  resize: vertical;
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm};
    border-radius: ${BorderRadius.sm};
  }
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
  
  &::placeholder {
    color: ${ThemeColors.text.hint};
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
  flex-wrap: wrap;
`;

const UploadButton = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;
  min-width: 150px;
`;

const UploadButtonLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${ThemeColors.background.paper};
  color: ${ThemeColors.text.primary};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  
  @media (max-width: 768px) {
    padding: ${Spacing.xs} ${Spacing.sm};
    border-radius: ${BorderRadius.sm};
    font-size: ${Typography.fontSize.xs};
  }
  
  &:hover {
    background-color: ${ThemeColors.gray[100]};
  }
`;

const SectionTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  margin-bottom: ${Spacing.md};
  color: ${ThemeColors.text.primary};
`;

const CompetitorsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
  
  @media (min-width: 769px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CompetitorItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const CompetitorCard = styled.div<{ isChecked: boolean }>`
  border: 1px solid ${props => props.isChecked ? ThemeColors.primary.main : ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.md};
  background-color: ${props => props.isChecked ? `${ThemeColors.primary.light}15` : ThemeColors.background.paper};
  transition: all 0.2s ease;
  width: 100%;
  min-width: 0;
  
  &:hover {
    border-color: ${ThemeColors.primary.main};
    box-shadow: ${Shadows.sm};
  }
`;

const CompetitorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  cursor: pointer;
`;

const CompetitorDetails = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  margin-top: ${Spacing.md};
  padding-top: ${Spacing.md};
  border-top: 1px solid ${ThemeColors.border.light};
  animation: slideDown 0.2s ease-out;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CompetitorPhotoUpload = styled.div`
  margin-bottom: ${Spacing.md};
`;

const CompetitorPhotoLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
`;

const CompetitorPhotoButtons = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  flex-wrap: wrap;
`;

const CompetitorPhotoButton = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${ThemeColors.background.paper};
  color: ${ThemeColors.text.primary};
  border: 1px dashed ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${Typography.fontSize.sm};
  
  &:hover {
    border-color: ${ThemeColors.primary.main};
    background-color: ${ThemeColors.primary.light}20;
    color: ${ThemeColors.primary.main};
  }
`;

const CompetitorPhotoPreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.sm};
`;

const CompetitorPhotoPreviewItem = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: ${BorderRadius.md};
  overflow: hidden;
  border: 1px solid ${ThemeColors.border.light};
`;

const CompetitorStockSlider = styled.div`
  margin-top: ${Spacing.md};
`;

const CompetitorSliderLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
`;

const CompetitorSliderWrapper = styled.div`
  position: relative;
`;

const CompetitorSlider = styled.input.attrs({ type: 'range' })`
  width: 100%;
  height: 6px;
  border-radius: ${BorderRadius.md};
  background: ${ThemeColors.gray[200]};
  outline: none;
  -webkit-appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${ThemeColors.warning.main};
    cursor: pointer;
    box-shadow: ${Shadows.sm};
    transition: all 0.2s ease;
    
    &:hover {
      transform: scale(1.1);
      box-shadow: ${Shadows.md};
    }
  }
  
  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${ThemeColors.warning.main};
    cursor: pointer;
    border: none;
    box-shadow: ${Shadows.sm};
    transition: all 0.2s ease;
    
    &:hover {
      transform: scale(1.1);
      box-shadow: ${Shadows.md};
    }
  }
`;

const CompetitorSliderValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
`;

const CompetitorSliderValueDisplay = styled.span`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.warning.main};
  font-size: ${Typography.fontSize.sm};
`;

const CompetitorSliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.hint};
`;

const CompetitorInstructions = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  font-style: italic;
  margin-bottom: ${Spacing.md};
  padding: ${Spacing.sm};
  background-color: ${ThemeColors.info.light}20;
  border-radius: ${BorderRadius.md};
  border-left: 3px solid ${ThemeColors.info.main};
`;

const CheckboxContainer = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  cursor: pointer;
`;

const StyledCheckbox = styled.div<{ checked: boolean }>`
  width: 18px;
  height: 18px;
  border: 1px solid ${props => props.checked ? ThemeColors.primary.main : ThemeColors.border.medium};
  border-radius: ${BorderRadius.xs};
  background-color: ${props => props.checked ? ThemeColors.primary.main : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:after {
    content: '';
    display: ${props => props.checked ? 'block' : 'none'};
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    position: relative;
    top: -1px;
  }
`;

const CheckboxLabel = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
`;

const MediaPreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.lg};
`;

const MediaPreviewItem = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: ${BorderRadius.md};
  overflow: hidden;
  border: 1px solid ${ThemeColors.border.light};
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: ${Spacing.xs};
  right: ${Spacing.xs};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${ThemeColors.secondary.main}CC;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${ThemeColors.secondary.main};
  }
`;

const DocumentPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.lg};
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.sm};
  background-color: ${ThemeColors.gray[100]};
  border-radius: ${BorderRadius.md};
  border: 1px solid ${ThemeColors.border.light};
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  flex: 1;
  overflow: hidden;
`;

const DocumentName = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VisitRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${Spacing.sm};
  }
`;

const VisitLabel = styled.div`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  white-space: nowrap;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  background-color: white;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right ${Spacing.md} center;
  background-size: 16px;
  padding-right: ${Spacing.xl};
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
`;

const SubmitButton = styled.button<{ disabled?: boolean; uploading?: boolean }>`
  width: 100%;
  padding: ${Spacing.md};
  background-color: ${props => props.disabled ? ThemeColors.gray[300] : ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: ${BorderRadius.md};
  font-weight: ${Typography.fontWeight.semiBold};
  font-size: ${Typography.fontSize.base};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm};
    border-radius: ${BorderRadius.sm};
    font-size: ${Typography.fontSize.sm};
  }
  
  &:hover {
    background-color: ${props => props.disabled ? ThemeColors.gray[300] : ThemeColors.primary.dark};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light}40;
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const UploadStatus = styled.div<{ type: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm};
  border-radius: ${BorderRadius.md};
  background-color: ${props => props.type === 'success' ? ThemeColors.success.light : ThemeColors.error.light};
  color: ${props => props.type === 'success' ? '#000000' : ThemeColors.error.main};
  font-size: ${Typography.fontSize.sm};
  margin-bottom: ${Spacing.md};
`;

const UploadProgressContainer = styled.div`
  background-color: ${ThemeColors.primary.light};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.md};
  margin-bottom: ${Spacing.md};
`;

const UploadProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.sm};
`;

const UploadProgressLabel = styled.span`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.primary.dark};
`;

const UploadProgressPercent = styled.span`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.primary.main};
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 12px;
  background-color: ${ThemeColors.gray[200]};
  border-radius: 6px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: linear-gradient(90deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  border-radius: 6px;
  transition: width 0.3s ease;
`;

const UploadProgressSubtext = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  margin-top: ${Spacing.xs};
  text-align: center;
`;

const FileSizeInfo = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.hint};
  margin-left: ${Spacing.xs};
`;

const DetailedProgressContainer = styled.div`
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  overflow: hidden;
  margin-top: ${Spacing.md};
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const DetailedProgressHeader = styled.div`
  padding: ${Spacing.md};
  background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  color: white;
`;

const DetailedProgressTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.sm};
`;

const DetailedProgressTitleText = styled.span`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
`;

const DetailedProgressPercent = styled.span`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
`;

const DetailedProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: rgba(255,255,255,0.3);
  border-radius: 4px;
  overflow: hidden;
`;

const DetailedProgressBarFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background-color: white;
  border-radius: 4px;
  transition: width 0.4s ease;
`;

const DetailedProgressPhase = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: rgba(255,255,255,0.85);
  margin-top: ${Spacing.xs};
`;

const FileStatusList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  padding: ${Spacing.sm};
  background-color: ${ThemeColors.background.paper};
`;

const FileStatusRow = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.xs};
  color: ${props => {
    switch (props.status) {
      case 'complete': return ThemeColors.success.main;
      case 'error': return ThemeColors.error.main;
      case 'compressing': case 'uploading': case 'retrying': return ThemeColors.primary.main;
      default: return ThemeColors.text.hint;
    }
  }};
  background-color: ${props => {
    switch (props.status) {
      case 'error': return `${ThemeColors.error.light}`;
      case 'compressing': case 'uploading': case 'retrying': return `${ThemeColors.primary.light}20`;
      default: return 'transparent';
    }
  }};
  margin-bottom: 2px;
`;

const FileStatusName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileStatusDetail = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.hint};
  white-space: nowrap;
`;

const MiniProgressBar = styled.div`
  width: 60px;
  height: 4px;
  background-color: ${ThemeColors.gray[200]};
  border-radius: 2px;
  overflow: hidden;
  flex-shrink: 0;
`;

const MiniProgressFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background-color: ${ThemeColors.primary.main};
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const NetworkWarning = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${ThemeColors.warning.light};
  color: ${ThemeColors.warning.main};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  border-top: 1px solid ${ThemeColors.border.light};
`;

const UploadErrorSummary = styled.div`
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${ThemeColors.error.light};
  border-top: 1px solid ${ThemeColors.border.light};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.error.main};
`;

const SpinnerIcon = styled(Loader)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const CompressionInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.sm};
  background-color: ${ThemeColors.primary.light};
  border-radius: ${BorderRadius.md};
  margin-bottom: ${Spacing.md};
`;

const CompressionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  cursor: pointer;
`;

const CompressionToggle = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CompressionStats = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  text-align: right;
`;

const DropdownContainer = styled.div`
  position: relative;
  margin-bottom: ${Spacing.lg};
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  background-color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${ThemeColors.primary.main};
  }
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: ${Spacing.xs};
  background-color: white;
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  box-shadow: ${Shadows.md};
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${ThemeColors.gray[100]};
  }
`;

const ManagerContactCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.lg};
  padding: ${Spacing.sm};
  border-radius: ${BorderRadius.md};
  cursor: pointer;
  
  &:hover {
    background-color: ${ThemeColors.gray[50]};
  }
`;

const SliderContainer = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const SliderLabel = styled.label`
  display: block;
  margin-bottom: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
`;

const SliderWrapper = styled.div`
  position: relative;
  padding: ${Spacing.md} 0;
`;

const Slider = styled.input.attrs({ type: 'range' })`
  width: 100%;
  height: 8px;
  border-radius: ${BorderRadius.md};
  background: ${ThemeColors.gray[200]};
  outline: none;
  -webkit-appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${ThemeColors.primary.main};
    cursor: pointer;
    box-shadow: ${Shadows.sm};
    transition: all 0.2s ease;
    
    &:hover {
      transform: scale(1.1);
      box-shadow: ${Shadows.md};
    }
  }
  
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${ThemeColors.primary.main};
    cursor: pointer;
    border: none;
    box-shadow: ${Shadows.sm};
    transition: all 0.2s ease;
    
    &:hover {
      transform: scale(1.1);
      box-shadow: ${Shadows.md};
    }
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light}40;
  }
  
  &:focus::-moz-range-thumb {
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light}40;
  }
`;

const SliderValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const SliderValueDisplay = styled.span`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.primary.main};
  font-size: ${Typography.fontSize.md};
`;

const SliderDots = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${Spacing.xs};
  padding: 0 ${Spacing.xs};
`;

const SliderDot = styled.div<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.active ? ThemeColors.primary.main : ThemeColors.gray[300]};
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.2);
  }
`;

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.hint};
  padding: 0 ${Spacing.xs};
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md};
  margin-top: ${Spacing.md};
  color: ${ThemeColors.success.main};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  text-align: center;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MediaPreviewWithCompression = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.sm};
`;

const CompressionBadge = styled.div<{ type: 'lossless' | 'lossy' }>`
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  background-color: ${props => props.type === 'lossless' ? ThemeColors.success.light : ThemeColors.warning.light};
  color: ${props => props.type === 'lossless' ? ThemeColors.success.main : ThemeColors.warning.main};
`;

// Add this helper component to prevent memory leaks by properly revoking object URLs
const SafeImagePreview = ({ file, alt }: { file: File; alt: string }) => {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Create the URL once when the file is provided
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // CRITICAL: Revoke the URL when the component unmounts or file changes
    // This prevents Chrome from running out of memory on mobile devices
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  if (!previewUrl) return null;

  return <MediaImage src={previewUrl} alt={alt} />;
};

export const NotesSection: React.FC<NotesSectionProps> = ({ 
  onSubmitNote,
  defaultSalesRepName = '',
  storeId = ''
}) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [salesRepName, setSalesRepName] = useState(defaultSalesRepName);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [nextVisitDate, setNextVisitDate] = useState('Next Week');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [detailedProgress, setDetailedProgress] = useState<DetailedUploadProgress | null>(null);
  const compressionEnabled = true;
  const [noteSubmitted, setNoteSubmitted] = useState(false);
  const [storeLevel, setStoreLevel] = useState<number>(100);
  const [lowStockCategories, setLowStockCategories] = useState<string[]>([]);
  const [requestManagerContact, setRequestManagerContact] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const fileStorageService = FileStorageService.getInstance();
  const imageCompressionService = ImageCompressionService.getInstance();
  
  const lowStockOptions = [
    'Herbs',
    'Vegetables',
    'Flowers',
    'Jumbo/Value',
    'Organic',
    'Livingston',
    'Heritage'
  ];
  
  const [competitors, setCompetitors] = useState<CompetitorOption[]>([
    { id: 'mckenzie', name: 'Nexis Seeds is Exclusive', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'burpee', name: 'Burpee Seeds', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'fothergills', name: 'Fothergills Seeds', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'westCoast', name: 'West Coast Seeds', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'osc', name: 'OSC (Ontario Seed)', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'newfoundland', name: 'Newfoundland Seed', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'berton', name: 'Berton Seeds', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'lindenburg', name: 'Lindenburg Seeds', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'reneesGarden', name: 'Renee\'s Garden (West Coast Seed Brand)', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'cornucopia', name: 'Cornucopia Seed (West Coast Seed Brand)', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'aimersOrganic', name: 'Aimers Organic (OSC Brand)', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'northernSeeds', name: 'Northern Seeds', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'gaiaSeeds', name: 'Gaia Seeds', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'halifaxSeeds', name: 'Halifax Seeds', isChecked: false, photos: [], stockLevel: 100 },
    { id: 'others', name: 'Others', isChecked: false, photos: [], stockLevel: 100 },
  ]);
  const [otherCompetitorName, setOtherCompetitorName] = useState('');
  const [visitType, setVisitType] = useState<VisitType>('');
  const [visitReason, setVisitReason] = useState('');

  const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      
      // Validate files
      const validFiles: File[] = [];
      const errors: string[] = [];
      
      filesArray.forEach(file => {
        const validation = fileStorageService.validateFile(file, 10000); // No size limit for images
        if (validation.valid) {
          validFiles.push(file);
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });
      
      if (errors.length > 0) {
        setUploadStatus({
          type: 'error',
          message: `Some files were rejected: ${errors.join(', ')}`
        });
      }
      
      if (validFiles.length > 0) {
        setMediaFiles([...mediaFiles, ...validFiles]);
        setUploadStatus({
          type: 'success',
          message: t('notes.imagesAddedSuccessfully', `${validFiles.length} image(s) added successfully`, { count: validFiles.length })
        });
      }
      
      // Clear the input
      e.target.value = '';
    }
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      
      // Validate files
      const validFiles: File[] = [];
      const errors: string[] = [];
      
      filesArray.forEach(file => {
        const validation = fileStorageService.validateFile(file, 10000); // No size limit for documents
        if (validation.valid) {
          validFiles.push(file);
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });
      
      if (errors.length > 0) {
        setUploadStatus({
          type: 'error',
          message: `Some files were rejected: ${errors.join(', ')}`
        });
      }
      
      if (validFiles.length > 0) {
        setDocuments([...documents, ...validFiles]);
        setUploadStatus({
          type: 'success',
          message: t('notes.documentsAddedSuccessfully', `${validFiles.length} document(s) added successfully`, { count: validFiles.length })
        });
      }
      
      // Clear the input
      e.target.value = '';
    }
  };

  const handleCheckboxToggle = (id: string) => {
    setCompetitors(
      competitors.map(competitor => 
        competitor.id === id 
          ? { ...competitor, isChecked: !competitor.isChecked } 
          : competitor
      )
    );
  };

  const handleCompetitorPhotoUpload = (competitorId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      
      // Validate files
      const validFiles: File[] = [];
      const errors: string[] = [];
      
      filesArray.forEach(file => {
        const validation = fileStorageService.validateFile(file, 10000); // No size limit for images
        if (validation.valid) {
          validFiles.push(file);
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });
      
      if (errors.length > 0) {
        setUploadStatus({
          type: 'error',
          message: `Some files were rejected: ${errors.join(', ')}`
        });
      }
      
      if (validFiles.length > 0) {
        setCompetitors(
          competitors.map(competitor =>
            competitor.id === competitorId
              ? { ...competitor, photos: [...competitor.photos, ...validFiles] }
              : competitor
          )
        );
        setUploadStatus({
          type: 'success',
          message: t('notes.competitorPhotosAdded', `${validFiles.length} competitor display photo(s) added`, { count: validFiles.length })
        });
      }
      
      // Clear the input
      e.target.value = '';
    }
  };

  const handleRemoveCompetitorPhoto = (competitorId: string, photoIndex: number) => {
    setCompetitors(
      competitors.map(competitor =>
        competitor.id === competitorId
          ? { ...competitor, photos: competitor.photos.filter((_, idx) => idx !== photoIndex) }
          : competitor
      )
    );
  };

  const handleCompetitorStockLevelChange = (competitorId: string, value: number) => {
    setCompetitors(
      competitors.map(competitor =>
        competitor.id === competitorId
          ? { ...competitor, stockLevel: value }
          : competitor
      )
    );
  };

  const handleCompetitorDisplayPanelsChange = (competitorId: string, value: number | '') => {
    setCompetitors(
      competitors.map(competitor =>
        competitor.id === competitorId
          ? { ...competitor, displayPanels: value === '' ? undefined : value }
          : competitor
      )
    );
  };

  const handleLowStockToggle = (category: string) => {
    setLowStockCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async () => {
    if (note.trim() === '' || salesRepName.trim() === '') {
      setUploadStatus({
        type: 'error',
        message: t('notes.errorRequiredFields', 'Please enter both a note and a sales representative name')
      });
      return;
    }

    if (isUploading) return;

    // Check network before starting
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setUploadStatus({
        type: 'error',
        message: t('notes.noConnection', 'No internet connection. Please check your network and try again.')
      });
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);
    setDetailedProgress(null);

    const competitorPhotoCount = competitors
      .filter(c => c.isChecked && c.id !== 'mckenzie')
      .reduce((sum, c) => sum + c.photos.length, 0);
    const totalFileCount = mediaFiles.length + documents.length + competitorPhotoCount;

    const totalSizeMB = [...mediaFiles, ...documents, ...competitors.flatMap(c => c.photos)]
      .reduce((sum, f) => sum + f.size, 0) / (1024 * 1024);
    console.log(`📤 Starting upload: ${totalFileCount} files, ${totalSizeMB.toFixed(2)} MB total`);

    // Safety timeout scales with file count
    const MIN_TIMEOUT_MS = 180000;
    const MAX_TIMEOUT_MS = 1200000; // 20 minutes for many large files
    const MS_PER_FILE = 30000;
    const SAFETY_TIMEOUT_MS = Math.min(
      MAX_TIMEOUT_MS,
      Math.max(MIN_TIMEOUT_MS, totalFileCount * MS_PER_FILE)
    );

    let timeoutFired = false;
    const uploadTimeout = setTimeout(() => {
      timeoutFired = true;
      setIsUploading(false);
      setDetailedProgress(null);
      setUploadStatus({
        type: 'error',
        message: t('notes.uploadTimeout', 'Upload timed out. Please check your connection and try again. If the problem persists, try uploading fewer files at once.')
      });
    }, SAFETY_TIMEOUT_MS);

    try {
      const selectedCompetitors = competitors
        .filter(competitor => competitor.isChecked)
        .map(competitor => competitor.name);
      
      const competitorData: CompetitorData[] = competitors
        .filter(competitor => competitor.isChecked && competitor.id !== 'mckenzie')
        .map(competitor => ({
          id: competitor.id,
          name: competitor.id === 'others' && otherCompetitorName ? otherCompetitorName : competitor.name,
          photos: competitor.photos,
          stockLevel: competitor.stockLevel,
          ...(competitor.displayPanels !== undefined && competitor.displayPanels > 0 ? { displayPanels: competitor.displayPanels } : {}),
        }));

      // Real progress callback — updates UI in real-time
      const handleUploadProgress = (progress: DetailedUploadProgress) => {
        setDetailedProgress(progress);
      };

      await onSubmitNote(
        note, 
        salesRepName, 
        selectedCompetitors, 
        { media: mediaFiles, documents }, 
        nextVisitDate,
        compressionEnabled,
        storeLevel,
        lowStockCategories,
        requestManagerContact,
        competitorData,
        visitType,
        visitType === 'other' ? visitReason : undefined,
        totalFileCount > 0 ? handleUploadProgress : undefined
      );
    
      clearTimeout(uploadTimeout);
      if (timeoutFired) return;

      // Check if there were partial failures from the detailed progress
      const failedCount = detailedProgress?.failedFiles || 0;
      const succeededCount = (detailedProgress?.completedFiles || totalFileCount);
    
      setNote('');
      setMediaFiles([]);
      setDocuments([]);
      setCompetitors(competitors.map(comp => ({ ...comp, isChecked: false, photos: [], stockLevel: 100, displayPanels: undefined })));
      setOtherCompetitorName('');
      setVisitType('');
      setVisitReason('');
      setStoreLevel(100);
      setLowStockCategories([]);
      setRequestManagerContact(false);
      setNoteSubmitted(true);
      setDetailedProgress(null);

      if (failedCount > 0) {
        setUploadStatus({
          type: 'error',
          message: `Note saved. ${succeededCount} of ${totalFileCount} files uploaded — ${failedCount} file(s) failed.`
        });
      } else {
        setUploadStatus(null);
      }
      
      setTimeout(() => setNoteSubmitted(false), 3000);
      
    } catch (error: any) {
      clearTimeout(uploadTimeout);
      if (timeoutFired) return;
      
      console.error('Failed to submit note:', error);
      
      let errorMessage = t('notes.submitError', 'Failed to submit note. Please try again.');
      if (error?.message) {
        if (error.message.includes('No internet connection') || error.message.includes('network')) {
          errorMessage = t('notes.noConnection', 'No internet connection. Please check your network and try again.');
        } else if (error.message.includes('timeout')) {
          errorMessage = t('notes.uploadTimeout', 'Upload timed out. Please check your connection and try again. If the problem persists, try uploading fewer files at once.');
        } else if (error.message.includes('Failed to upload') || error.message.includes('All file uploads failed')) {
          errorMessage = `${t('notes.uploadError', 'Some files failed to upload')}: ${error.message}`;
        } else {
          errorMessage = `${errorMessage} (${error.message})`;
        }
      }
      
      setUploadStatus({ type: 'error', message: errorMessage });
    } finally {
      clearTimeout(uploadTimeout);
      if (!timeoutFired) {
        setIsUploading(false);
      }
    }
  };

  const handleRemoveMedia = (index: number) => {
    const updatedMediaFiles = [...mediaFiles];
    updatedMediaFiles.splice(index, 1);
    setMediaFiles(updatedMediaFiles);
  };

  const handleRemoveDocument = (index: number) => {
    const updatedDocuments = [...documents];
    updatedDocuments.splice(index, 1);
    setDocuments(updatedDocuments);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('notes.title')}</CardTitle>
      </CardHeader>
      <CardBody>
        {/* First Row: Sales Rep Name and Visit Type side by side */}
        <FormRow>
          <FormGroup style={{ marginBottom: 0 }}>
            <FormLabel>{t('notes.salesRepName')}:</FormLabel>
            <Input
              type="text"
              value={salesRepName}
              onChange={(e) => setSalesRepName(e.target.value)}
              placeholder={t('notes.enterName')}
            />
          </FormGroup>
          <FormGroup style={{ marginBottom: 0 }}>
            <FormLabel>{t('notes.visitType', 'Visit Type')}:</FormLabel>
            <Select
              value={visitType}
              onChange={(e) => {
                setVisitType(e.target.value as VisitType);
                if (e.target.value !== 'other') {
                  setVisitReason(''); // Clear reason when switching away from 'other'
                }
              }}
            >
              {VISIT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey, option.defaultLabel)}
                </option>
              ))}
            </Select>
          </FormGroup>
        </FormRow>
        
        {visitType === 'other' && (
          <FormGroup>
            <FormLabel>{t('notes.visitReason', 'Reason for Visit')}:</FormLabel>
            <Input
              type="text"
              value={visitReason}
              onChange={(e) => setVisitReason(e.target.value)}
              placeholder={t('notes.enterVisitReason', 'Enter reason for visit...')}
            />
          </FormGroup>
        )}
        
        <FormGroup>
          <FormLabel>{t('notes.note')}:</FormLabel>
          <TextArea
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              // Clear success message when user starts typing
              if (noteSubmitted) {
                setNoteSubmitted(false);
              }
            }}
            placeholder={t('notes.leaveNote')}
            style={{ minHeight: '100px' }}
          />
        </FormGroup>

        {/* Upload Status (shown when not uploading) */}
        {uploadStatus && !isUploading && (
          <UploadStatus type={uploadStatus.type}>
            {uploadStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {uploadStatus.message}
          </UploadStatus>
        )}


        {/* Media Previews */}
        {mediaFiles.length > 0 && (
          <MediaPreviewContainer>
            {mediaFiles.map((file, index) => {
              const isImage = imageCompressionService.canCompress(file);
              const recommendations = isImage ? imageCompressionService.getCompressionRecommendations(file) : null;
              
              return (
                <MediaPreviewWithCompression key={`media-${index}`}>
                  <MediaPreviewItem>
                    <SafeImagePreview 
                      file={file} 
                      alt={`Preview ${index}`} 
                    />
                    <RemoveButton onClick={() => handleRemoveMedia(index)}>
                      <X size={14} />
                    </RemoveButton>
                  </MediaPreviewItem>
                  <div>
                    <div style={{ fontSize: Typography.fontSize.sm, fontWeight: Typography.fontWeight.medium }}>
                      {file.name}
                    </div>
                    <FileSizeInfo>
                      {fileStorageService.formatFileSize(file.size)}
                    </FileSizeInfo>
                  </div>
                </MediaPreviewWithCompression>
              );
            })}
          </MediaPreviewContainer>
        )}

        {/* Document Previews */}
        {documents.length > 0 && (
          <DocumentPreviewContainer>
            {documents.map((file, index) => (
              <DocumentItem key={`doc-${index}`}>
                <DocumentInfo>
                  <FileText size={18} />
                  <DocumentName>
                    {file.name}
                    <FileSizeInfo>({fileStorageService.formatFileSize(file.size)})</FileSizeInfo>
                  </DocumentName>
                </DocumentInfo>
                <RemoveButton onClick={() => handleRemoveDocument(index)}>
                  <X size={14} />
                </RemoveButton>
              </DocumentItem>
            ))}
          </DocumentPreviewContainer>
        )}

        <ButtonsRow>
          <UploadButton>
            <UploadButtonLabel htmlFor="media-upload">
              <Camera size={18} />
              {t('notes.uploadMedia', 'Upload Photo')}
              <input
                type="file"
                id="media-upload"
                accept="image/*"
                onChange={handleMediaUpload}
                style={{ display: 'none' }}
                multiple
              />
            </UploadButtonLabel>
          </UploadButton>

          <UploadButton>
            <UploadButtonLabel htmlFor="document-upload">
              <FileText size={18} />
              {t('notes.uploadDocument', 'Upload Document')}
              <input
                type="file"
                id="document-upload"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                onChange={handleDocumentUpload}
                style={{ display: 'none' }}
                multiple
              />
            </UploadButtonLabel>
          </UploadButton>
        </ButtonsRow>

        {/* Store Level and Low Stock Category in two columns */}
        <FormRow>
          <SliderContainer style={{ marginBottom: 0 }}>
            <SliderLabel>
              {t('notes.storeLevel', 'Package Level in Store Display')}:
            </SliderLabel>
            <SliderWrapper>
              <Slider
                min={0}
                max={100}
                value={storeLevel}
                onChange={(e) => setStoreLevel(Number(e.target.value))}
              />
              <SliderValue>
                <span>{t('notes.approximateLevel', 'Approximate Level')}</span>
                <SliderValueDisplay>{storeLevel}%</SliderValueDisplay>
              </SliderValue>
              <SliderDots>
                {[0, 25, 50, 75, 100].map((value) => (
                  <SliderDot
                    key={value}
                    active={Math.abs(storeLevel - value) < 12.5}
                    onClick={() => setStoreLevel(value)}
                    title={`${value}%`}
                  />
                ))}
              </SliderDots>
              <SliderLabels>
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </SliderLabels>
            </SliderWrapper>
          </SliderContainer>

          {/* Low Stock Category Dropdown */}
          <FormGroup style={{ marginBottom: 0 }}>
            <FormLabel>Low Stock Category:</FormLabel>
            <DropdownContainer ref={dropdownRef}>
              <DropdownButton
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span>
                  {lowStockCategories.length === 0
                    ? 'Select categories...'
                    : `${lowStockCategories.length} category(ies) selected`}
                </span>
                <ChevronDown size={18} style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
              </DropdownButton>
              <DropdownMenu isOpen={isDropdownOpen}>
                {lowStockOptions.map((category) => (
                  <DropdownItem
                    key={category}
                    onClick={() => handleLowStockToggle(category)}
                  >
                    <CheckboxContainer>
                      <HiddenCheckbox
                        checked={lowStockCategories.includes(category)}
                        onChange={() => handleLowStockToggle(category)}
                      />
                      <StyledCheckbox checked={lowStockCategories.includes(category)} />
                    </CheckboxContainer>
                    <CheckboxLabel>{category}</CheckboxLabel>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </DropdownContainer>
          </FormGroup>
        </FormRow>

        {/* Request Manager Contact Checkbox */}
        <ManagerContactCheckbox onClick={() => setRequestManagerContact(!requestManagerContact)}>
          <CheckboxContainer>
            <HiddenCheckbox
              checked={requestManagerContact}
              onChange={() => setRequestManagerContact(!requestManagerContact)}
            />
            <StyledCheckbox checked={requestManagerContact} />
          </CheckboxContainer>
          <CheckboxLabel>Request Manager Contact</CheckboxLabel>
        </ManagerContactCheckbox>

        <SectionTitle>{t('notes.competitors', 'Competitors')}:</SectionTitle>
        <CompetitorInstructions>
          {t('notes.competitorInstructions', 'Please take a photo of all competitive displays in store (seed/seed starting). When you check a competitor, you can upload photos and rate their stock level.')}
        </CompetitorInstructions>
        <CompetitorsGrid>
          {competitors.map((competitor) => (
            <CompetitorCard key={competitor.id} isChecked={competitor.isChecked}>
              <CompetitorHeader onClick={() => handleCheckboxToggle(competitor.id)}>
                <CheckboxContainer>
                  <HiddenCheckbox 
                    checked={competitor.isChecked}
                    onChange={() => handleCheckboxToggle(competitor.id)}
                  />
                  <StyledCheckbox checked={competitor.isChecked} />
                </CheckboxContainer>
                <CheckboxLabel>{competitor.name}</CheckboxLabel>
              </CompetitorHeader>
              
              {/* Show photo upload and stock level when competitor is checked (except McKenzie Exclusive) */}
              {competitor.isChecked && competitor.id !== 'mckenzie' && (
                <CompetitorDetails isOpen={competitor.isChecked}>
                  {/* Other competitor name input */}
                  {competitor.id === 'others' && (
                    <FormGroup style={{ marginBottom: Spacing.md }}>
                      <Input
                        placeholder={t('notes.specifyCompetitor', 'Specify competitor name...')}
                        value={otherCompetitorName}
                        onChange={(e) => setOtherCompetitorName(e.target.value)}
                      />
                    </FormGroup>
                  )}
                  
                  {/* Photo Upload Section */}
                  <CompetitorPhotoUpload>
                    <CompetitorPhotoLabel>
                      <Camera size={16} />
                      {t('notes.uploadDisplayPhotos', 'Upload Display Photos')}
                    </CompetitorPhotoLabel>
                    <CompetitorPhotoButtons>
                      <CompetitorPhotoButton htmlFor={`competitor-photo-camera-${competitor.id}`}>
                        <Camera size={16} />
                        {t('notes.takePhoto', 'Take Photo')}
                        <input
                          type="file"
                          id={`competitor-photo-camera-${competitor.id}`}
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handleCompetitorPhotoUpload(competitor.id, e)}
                          style={{ display: 'none' }}
                        />
                      </CompetitorPhotoButton>
                      <CompetitorPhotoButton htmlFor={`competitor-photo-library-${competitor.id}`}>
                        <Upload size={16} />
                        {t('notes.fromLibrary', 'From Library')}
                        <input
                          type="file"
                          id={`competitor-photo-library-${competitor.id}`}
                          accept="image/*"
                          onChange={(e) => handleCompetitorPhotoUpload(competitor.id, e)}
                          style={{ display: 'none' }}
                          multiple
                        />
                      </CompetitorPhotoButton>
                    </CompetitorPhotoButtons>
                    
                    {/* Photo Previews */}
                    {competitor.photos.length > 0 && (
                      <CompetitorPhotoPreviewContainer>
                        {competitor.photos.map((photo, index) => (
                          <CompetitorPhotoPreviewItem key={`photo-${competitor.id}-${index}`}>
                            <SafeImagePreview 
                              file={photo} 
                              alt={`${competitor.name} display ${index + 1}`} 
                            />
                            <RemoveButton onClick={() => handleRemoveCompetitorPhoto(competitor.id, index)}>
                              <X size={12} />
                            </RemoveButton>
                          </CompetitorPhotoPreviewItem>
                        ))}
                      </CompetitorPhotoPreviewContainer>
                    )}
                  </CompetitorPhotoUpload>
                  
                  {/* Stock Level Slider */}
                  <CompetitorStockSlider>
                    <CompetitorSliderLabel>
                      {t('notes.competitorStockLevel', 'Stock Level')}
                    </CompetitorSliderLabel>
                    <CompetitorSliderWrapper>
                      <CompetitorSlider
                        min={0}
                        max={100}
                        value={competitor.stockLevel}
                        onChange={(e) => handleCompetitorStockLevelChange(competitor.id, Number(e.target.value))}
                      />
                      <CompetitorSliderValue>
                        <span>{t('notes.approximateLevel', 'Approximate Level')}</span>
                        <CompetitorSliderValueDisplay>{competitor.stockLevel}%</CompetitorSliderValueDisplay>
                      </CompetitorSliderValue>
                      <CompetitorSliderLabels>
                        <span>0%</span>
                        <span>25%</span>
                        <span>50%</span>
                        <span>75%</span>
                        <span>100%</span>
                      </CompetitorSliderLabels>
                    </CompetitorSliderWrapper>
                  </CompetitorStockSlider>

                  {/* Display Panels Dropdown */}
                  <CompetitorStockSlider>
                    <CompetitorSliderLabel>
                      {t('notes.competitorDisplayPanels', 'How many panels are on the competitor display?')}
                    </CompetitorSliderLabel>
                    <Select
                      value={competitor.displayPanels ?? ''}
                      onChange={(e) => handleCompetitorDisplayPanelsChange(competitor.id, e.target.value === '' ? '' : Number(e.target.value))}
                    >
                      <option value="">{t('notes.selectPanels', 'Select...')}</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </Select>
                  </CompetitorStockSlider>
                </CompetitorDetails>
              )}
            </CompetitorCard>
          ))}
        </CompetitorsGrid>

        <VisitRow>
          <VisitLabel>{t('notes.nextVisit')}:</VisitLabel>
          <Select
            value={nextVisitDate}
            onChange={(e) => setNextVisitDate(e.target.value)}
          >
            <option value="Next Week">{t('notes.nextWeek', 'Next Week')}</option>
            <option value="+2 Weeks">{t('notes.plus2Weeks', '+2 Weeks')}</option>
            <option value="+3 Weeks">{t('notes.plus3Weeks', '+3 Weeks')}</option>
            <option value="+4 Weeks">{t('notes.plus4Weeks', '+4 Weeks')}</option>
            <option value="+5 Weeks">{t('notes.plus5Weeks', '+5 Weeks')}</option>
          </Select>
        </VisitRow>

        <SubmitButton 
          onClick={handleSubmit}
          disabled={isUploading}
          uploading={isUploading}
        >
          {isUploading ? (
            <>
              <SpinnerIcon size={16} />
              {t('notes.uploading', 'Uploading...')}
            </>
          ) : (
            t('notes.submitNote')
          )}
        </SubmitButton>

        {/* Detailed Upload Progress — under the submit button */}
        {isUploading && detailedProgress && detailedProgress.totalFiles > 0 && (
          <DetailedProgressContainer>
            <DetailedProgressHeader>
              <DetailedProgressTitle>
                <DetailedProgressTitleText>
                  {detailedProgress.completedFiles + detailedProgress.failedFiles} of {detailedProgress.totalFiles} files
                </DetailedProgressTitleText>
                <DetailedProgressPercent>
                  {detailedProgress.overallProgress}%
                </DetailedProgressPercent>
              </DetailedProgressTitle>
              <DetailedProgressBar>
                <DetailedProgressBarFill progress={detailedProgress.overallProgress} />
              </DetailedProgressBar>
              {detailedProgress.currentPhase && (
                <DetailedProgressPhase>{detailedProgress.currentPhase}</DetailedProgressPhase>
              )}
            </DetailedProgressHeader>

            <FileStatusList>
              {detailedProgress.fileStatuses.map((file, idx) => (
                <FileStatusRow key={`${file.name}-${idx}`} status={file.status}>
                  {file.status === 'complete' && <CheckCircle size={14} />}
                  {file.status === 'error' && <AlertCircle size={14} />}
                  {file.status === 'compressing' && <SpinnerIcon size={14} />}
                  {file.status === 'uploading' && <Upload size={14} />}
                  {file.status === 'retrying' && <RefreshCw size={14} />}
                  {file.status === 'pending' && <span style={{ width: 14, height: 14, display: 'inline-block', borderRadius: '50%', border: `2px solid ${ThemeColors.gray[300]}` }} />}
                  
                  <FileStatusName>{file.name}</FileStatusName>

                  {file.status === 'complete' && file.compressedSize && file.originalSize > 0 && file.compressedSize < file.originalSize && (
                    <FileStatusDetail>
                      {fileStorageService.formatFileSize(file.originalSize)} → {fileStorageService.formatFileSize(file.compressedSize)}
                    </FileStatusDetail>
                  )}
                  {file.status === 'complete' && (!file.compressedSize || file.compressedSize >= file.originalSize) && file.originalSize > 0 && (
                    <FileStatusDetail>
                      {fileStorageService.formatFileSize(file.originalSize)}
                    </FileStatusDetail>
                  )}
                  {file.status === 'compressing' && (
                    <FileStatusDetail>{t('notes.compressing', 'Compressing...')}</FileStatusDetail>
                  )}
                  {(file.status === 'uploading') && (
                    <MiniProgressBar>
                      <MiniProgressFill progress={file.progress} />
                    </MiniProgressBar>
                  )}
                  {file.status === 'retrying' && (
                    <FileStatusDetail>{t('notes.retrying', 'Retrying...')}</FileStatusDetail>
                  )}
                  {file.status === 'error' && file.error && (
                    <FileStatusDetail>{file.error.length > 30 ? file.error.slice(0, 30) + '…' : file.error}</FileStatusDetail>
                  )}
                </FileStatusRow>
              ))}
            </FileStatusList>

            {!detailedProgress.isOnline && (
              <NetworkWarning>
                <WifiOff size={14} />
                {t('notes.offlineWarning', 'You appear to be offline. Uploads will resume when connection is restored.')}
              </NetworkWarning>
            )}

            {detailedProgress.failedFiles > 0 && (
              <UploadErrorSummary>
                <AlertCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                {detailedProgress.failedFiles} file(s) failed. The note will be saved with the files that succeeded.
              </UploadErrorSummary>
            )}
          </DetailedProgressContainer>
        )}

        {/* Success Message */}
        {noteSubmitted && (
          <SuccessMessage>
            <CheckCircle size={20} />
            {t('notes.noteSubmitted')}
          </SuccessMessage>
        )}
      </CardBody>
    </Card>
  );
};